import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../store/editorStore';
import { saveProject, syncSetLastActive } from '../engine/projectStorage';
import { capturePreviewThumbnail } from '../engine/thumbnailCapture';
import { announceProjectOpen, announceProjectSave, closeTabSync } from '../engine/tabSync';

const DEBOUNCE_MS = 2000;
const INITIAL_THUMBNAIL_DELAY_MS = 4000;

/**
 * Auto-saves the project to IndexedDB whenever the store changes (debounced 2s).
 * Also writes lastActiveProjectId to localStorage on beforeunload.
 */
export function useAutoSave() {
  const { t } = useTranslation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Announce this tab is editing the project
  useEffect(() => {
    const projectId = useEditorStore.getState().project.id;
    announceProjectOpen(projectId);
    return () => closeTabSync();
  }, []);

  // Delayed initial thumbnail capture — ensures video frame is decoded first
  useEffect(() => {
    const initialTimer = setTimeout(() => {
      const project = useEditorStore.getState().project;
      const thumbnail = capturePreviewThumbnail();
      if (thumbnail) {
        saveProject(project, thumbnail).then(() => {
          announceProjectSave(project.id);
        }).catch(() => {});
      }
    }, INITIAL_THUMBNAIL_DELAY_MS);
    return () => clearTimeout(initialTimer);
  }, []);

  useEffect(() => {
    let prevProject = useEditorStore.getState().project;
    const unsub = useEditorStore.subscribe((state) => {
      const project = state.project;
      if (project === prevProject) return;
      prevProject = project;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const thumbnail = capturePreviewThumbnail();
        saveProject(project, thumbnail).then(() => {
          announceProjectSave(project.id);
        }).catch((err) => {
          if (err?.name === 'QuotaExceededError') {
            toast.error(t('editor.autoSaveError', 'Auto-save failed: storage full'));
          } else {
            console.error('Auto-save error:', err);
          }
        });
      }, DEBOUNCE_MS);
    });

    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [t]);

  // beforeunload: sync-write lastActive so crash recovery works
  useEffect(() => {
    const handler = () => {
      const id = useEditorStore.getState().project.id;
      syncSetLastActive(id);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
